import React, { useEffect, useMemo, useState } from "react";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { buildPlayerWindowTitle } from "../../../../utils/playerTitle.js";
import DragBox2026 from "../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";
import "./cflStats.style.scss";

const CFL_STATS_BASE_URL = "https://stats.cfl.ca/stats/player/";
const ESPN_PROFILE_BASE_URL = "https://www.espn.com/nfl/player/_/id/";
const ESPN_PROFILE_API_URL = "https://site.api.espn.com/apis/common/v3/sports/football/nfl/athletes/";
const PLAYER_STATS_ENDPOINT = "https://www.fbxchange.com/ReactPHP/playerStats.php";
const DEFAULT_POS = { x: 80, y: 95 };

export const normalizeCflPlayerId = (value) => {
  const normalized = String(value ?? "").trim();
  return /^\d+$/.test(normalized) && normalized !== "0" ? normalized : "";
};

export const normalizeEspnPlayerId = (value) => {
  const normalized = String(value ?? "").trim();
  return /^\d+$/.test(normalized) && normalized !== "0" ? normalized : "";
};

export const resolveStatsSource = (preferredSource, espnPlayerId) => (
  preferredSource === "ESPN" && normalizeEspnPlayerId(espnPlayerId) ? "ESPN" : "CFL"
);

const CFLStats = () => {
  const { activePlayer, playerData, playerID } = usePlayer();
  const [dataSource, setDataSource] = useState("CFL");
  const [espnProfile, setEspnProfile] = useState(null);
  const [espnProfileStatus, setEspnProfileStatus] = useState("idle");

  const currentPlayer = useMemo(() => {
    const activeId = activePlayer?.FBXID ?? playerID;
    const players = Array.isArray(playerData) ? playerData : [];
    const matchedPlayer = players.find(
      (player) => String(player?.FBXID ?? "") === String(activeId ?? "")
    );

    return matchedPlayer || activePlayer || {};
  }, [activePlayer, playerData, playerID]);

  const cflPlayerId = normalizeCflPlayerId(
    currentPlayer?.cflId ?? currentPlayer?.CFLID ?? currentPlayer?.cflid
  );
  const espnPlayerId = normalizeEspnPlayerId(
    currentPlayer?.espnId ?? currentPlayer?.espn_id ?? currentPlayer?.ESPNID
  );
  const activeDataSource = resolveStatsSource(dataSource, espnPlayerId);

  useEffect(() => {
    if (activeDataSource !== "ESPN" || !espnPlayerId) return undefined;

    const controller = new AbortController();
    setEspnProfile(null);
    setEspnProfileStatus("loading");
    const fetchEspnProfile = async () => {
      try {
        const proxyResponse = await fetch(PLAYER_STATS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ espnId: espnPlayerId }),
          signal: controller.signal,
        });
        const proxyData = await proxyResponse.json();
        if (proxyResponse.ok && proxyData?.ok && proxyData?.athlete) return proxyData.athlete;
      } catch (error) {
        if (error.name === "AbortError") throw error;
        console.warn("FBX ESPN profile proxy failed; trying ESPN directly:", error);
      }

      const directResponse = await fetch(`${ESPN_PROFILE_API_URL}${espnPlayerId}`, {
        signal: controller.signal,
      });
      if (!directResponse.ok) throw new Error(`ESPN profile request failed: ${directResponse.status}`);
      const directData = await directResponse.json();
      if (!directData?.athlete) throw new Error("ESPN profile was not found.");
      return directData.athlete;
    };

    fetchEspnProfile()
      .then((athlete) => {
        setEspnProfile(athlete);
        setEspnProfileStatus("ready");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        console.warn("ESPN profile request failed:", error);
        setEspnProfileStatus("error");
      });

    return () => controller.abort();
  }, [activeDataSource, espnPlayerId]);

  const sourceId = activeDataSource === "ESPN" ? espnPlayerId : cflPlayerId;
  const statsUrl = sourceId
    ? `${activeDataSource === "ESPN" ? ESPN_PROFILE_BASE_URL : CFL_STATS_BASE_URL}${sourceId}`
    : "";
  const playerTitle = buildPlayerWindowTitle(currentPlayer, "Player Stats");

  return (
    <DragBox2026
      storageKey="CFL_STATS_BOX_LOCATION"
      defaultPos={DEFAULT_POS}
      title="Player Stats - "
      countText={playerTitle}
      minWidth="min(1100px, calc(100vw - 24px))"
      maxWidth="min(1100px, calc(100vw - 24px))"
      zIndex={1200}
      showReset={true}
      className="card shadow-sm cfl-stats-window"
    >
      {cflPlayerId || espnPlayerId ? (
        <div className="cfl-stats-content">
          <div className="cfl-stats-toolbar">
            <div className="cfl-stats-source" role="group" aria-label="Stats data source">
              <button
                type="button"
                className={activeDataSource === "CFL" ? "cfl-stats-source__option cfl-stats-source__option--active" : "cfl-stats-source__option"}
                onClick={() => setDataSource("CFL")}
                disabled={!cflPlayerId}
              >
                CFL Data
              </button>
              <button
                type="button"
                className={activeDataSource === "ESPN" ? "cfl-stats-source__option cfl-stats-source__option--active" : "cfl-stats-source__option"}
                onClick={() => setDataSource("ESPN")}
                disabled={!espnPlayerId}
              >
                ESPN Data
              </button>
            </div>
            <span className="text-muted">{activeDataSource} ID: {sourceId}</span>
            <a
              className="btn btn-sm btn-outline-primary"
              href={statsUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open on {activeDataSource === "ESPN" ? "ESPN" : "CFL Stats"}
            </a>
          </div>

          {statsUrl && activeDataSource === "CFL" ? (
            <div className="cfl-stats-frame-wrap">
              <iframe
                key={statsUrl}
                className="cfl-stats-frame"
                src={statsUrl}
                title={`${playerTitle} ${activeDataSource} profile`}
              />
            </div>
          ) : statsUrl && activeDataSource === "ESPN" ? (
            <div className="cfl-stats-espn-profile">
              {espnProfileStatus === "loading" && <div className="cfl-stats-empty">Loading ESPN profile...</div>}
              {espnProfileStatus === "error" && (
                <div className="cfl-stats-empty">ESPN profile details could not be retrieved.</div>
              )}
              {espnProfileStatus === "ready" && espnProfile && (
                <>
                  <header className="cfl-stats-espn-header">
                    {espnProfile.headshot?.href && (
                      <img src={espnProfile.headshot.href} alt={espnProfile.headshot.alt || espnProfile.displayName} />
                    )}
                    <div>
                      <strong>{espnProfile.displayName}</strong>
                      <span>
                        {[espnProfile.displayJersey, espnProfile.position?.displayName, espnProfile.team?.displayName]
                          .filter(Boolean).join(" | ")}
                      </span>
                      <span>ESPN ID: {espnPlayerId}</span>
                    </div>
                  </header>

                  <div className="cfl-stats-espn-details">
                    <div><span>Status</span><strong>{espnProfile.status?.name || "--"}</strong></div>
                    <div><span>Team</span><strong>{espnProfile.team?.abbreviation || "--"}</strong></div>
                    <div><span>Position</span><strong>{espnProfile.position?.abbreviation || "--"}</strong></div>
                    <div><span>Height</span><strong>{espnProfile.displayHeight || "--"}</strong></div>
                    <div><span>Weight</span><strong>{espnProfile.displayWeight || "--"}</strong></div>
                    <div><span>DOB</span><strong>{espnProfile.displayDOB || "--"}</strong></div>
                    <div><span>Age</span><strong>{espnProfile.age ?? "--"}</strong></div>
                    <div><span>Experience</span><strong>{espnProfile.displayExperience || "--"}</strong></div>
                    <div><span>College</span><strong>{espnProfile.college?.name || "--"}</strong></div>
                  </div>

                  {espnProfile.statsSummary?.statistics?.length > 0 && (
                    <section className="cfl-stats-espn-stats">
                      <strong>{espnProfile.statsSummary.displayName || "ESPN statistics"}</strong>
                      <div>
                        {espnProfile.statsSummary.statistics.map((stat) => (
                          <article key={stat.name}>
                            <span>{stat.abbreviation || stat.shortDisplayName}</span>
                            <strong>{stat.displayValue}</strong>
                            <small>{stat.shortDisplayName || stat.displayName}</small>
                          </article>
                        ))}
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="cfl-stats-empty">This player does not have a valid {activeDataSource} ID.</div>
          )}
        </div>
      ) : (
        <div className="cfl-stats-empty">
          No CFL ID or ESPN ID is available for this player.
        </div>
      )}
    </DragBox2026>
  );
};

export default CFLStats;
